import { useState } from "react";
import { useQuery, useMutation } from "react-query";
import { Box, Paper, Typography, Button, Chip } from "@leap4/frontend";
import { AXIOS_INSTANCE } from "../../../api/axiosInstance";

type Seat = { key: string; host: string; ok: boolean; error: string | null; schemaChanged: boolean;
  operations: { name: string; ok: boolean; errors: string[] }[] };
type Status = { running: boolean; state: string; error?: string; result: null | { checkedAt: string; ok: boolean; error?: string; seats: Seat[] } };
const labels: Record<string, string> = { never_checked: "Not checked yet", stale: "Needs recheck", compatible: "Valid", failed: "Updates blocked" };

export default function CrossroadsMonitor() {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem("crossroads-monitor-expanded") === "true"; } catch { return false; }
  });
  const query = useQuery<Status>("crossroads-schema-monitor", async () => (await AXIOS_INSTANCE.get("/crossroads-tiering/monitor")).data,
    { refetchInterval: 30000, retry: false });
  const check = useMutation(async () => (await AXIOS_INSTANCE.post<Status>("/crossroads-tiering/monitor")).data,
    { onSuccess: () => query.refetch() });
  const data = query.data;
  const busy = check.isLoading || data?.running;
  return <Paper sx={{ m: 0, p: "14px", width: "100%", minWidth: 0, boxSizing: "border-box", textAlign: "left", borderRadius: "12px" }} elevation={1}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Crossroads Tiering Monitor</Typography>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Chip label={busy ? "Checking…" : check.isError ? "Check failed" : query.isError ? "Status unavailable" : data ? labels[data.state] : "Loading…"}
          color={!query.isError && !check.isError && data?.state === "compatible" ? "success" : "default"} />
        <Button variant="contained" disabled={!!busy || query.isLoading} onClick={() => check.mutate()}>
          {busy ? "Checking…" : "Check now"}
        </Button>
      </Box>
    </Box>
    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
      Check Crossroads integration operations. Checks run automatically before each update.
      Invalid or failed checks block tier list updates!
    </Typography>
    {(query.isError || check.isError) && <Typography role="alert" color="error" sx={{ mt: 2 }}>
      {check.isError ? "Check could not complete. Retry to get a current result." : "Could not load monitor status. Check your connection and access."}
    </Typography>}
    {(data?.error || data?.result?.error) && <Typography role="alert" color="error">{data.error || data.result?.error}</Typography>}
    {data?.result && <>
      <Typography variant="caption" color="textSecondary">Shared result · visible to all users</Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>Last completed check: {new Date(data.result.checkedAt).toLocaleString()}</Typography>
      {data.result.seats.length === 0 && <Typography color="error">No enabled seats were available to check.</Typography>}
      <details open={expanded} onToggle={event => {
        const open = event.currentTarget.open;
        setExpanded(open);
        try { localStorage.setItem("crossroads-monitor-expanded", String(open)); } catch { /* preference only */ }
      }}>
      <summary style={{ cursor: "pointer", marginTop: 8 }}>Seat and operation details</summary>
      {data.result.seats.map(seat => <Box key={seat.key} sx={{ mt: 2, pt: 1, borderTop: "1px solid #ddd" }}>
        <Typography sx={{ fontWeight: 600 }}>{seat.key.toUpperCase()} — {seat.ok ? "Compatible at last check" : "Failed"}</Typography>
        <Typography variant="body2" color="textSecondary">{seat.host}</Typography>
        {seat.schemaChanged && <Typography variant="body2">Remote schema changed since the previous check.</Typography>}
        {seat.error && <Typography color="error">{seat.error}</Typography>}
        {seat.operations.map(op => <Typography key={op.name} variant="body2" color={op.ok ? "textSecondary" : "error"}>
          {op.ok ? "✓" : "✕"} {op.name}{op.errors.length ? `: ${op.errors.join("; ")}` : ""}
        </Typography>)}
      </Box>)}
      </details>
    </>}
  </Paper>;
}
